// MyWalletPanel — Phase 4 wallet view inside the consolidated
// /profile hub. Mirrors the PlayerRequestsPanel structure:
//   1. Brand-magenta hero card — animated count-up of pool balance
//      (Phase 8.1 swap from indigo; Profile section took over the
//      magenta identity when Home took indigo)
//   2. Action row — Request Recharge + Request Withdrawal (inline
//      collapsible forms, no jarring modals)
//   3. Pending / History sub-tabs (size="sm" MagentaTabBar, brand accent)
//   4. Pending feed — agent's own pending recharge + withdrawal requests
//   5. History feed — wallet ledger (every credit/debit, chronological)
//
// Wallet sync — refreshAgentWallet on mount + after any successful
// request submission. The form components handle their own server
// roundtrips; this panel re-fetches the local wallet snapshot
// + pending list afterward so the page reflects the new state.

import { ReactNode, useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import {
  refreshAgentWallet,
  useAgentWallet,
} from '@baaligames/shared-ui/admin/lib/agentWallet';
import { useCountUp } from '@baaligames/shared-ui/admin/lib/useCountUp';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import Icon from '@baaligames/shared-ui/components/Icon';
import MagentaTabBar from '@baaligames/shared-ui/admin/components/MagentaTabBar';
import RequestRechargeForm from '../../components/RequestRechargeForm';
import RequestWithdrawalForm from '../../components/RequestWithdrawalForm';

// ─── Types ─────────────────────────────────────────────────────────

type LedgerRow = {
  id: number; admin_id: number; kind: string; amount: number;
  source_user_id: number | null; source_ref: string | null;
  note: string | null; balance_after: number | null; created_at: string;
};

// U4 (2026-07-09): the deposit-ledger types/feed moved out with the
// duplicate deposit sub-tab — /profile/deposit (MyDepositPanel) is the
// single home of the security-deposit surface now.

type PendingRechargeRow = {
  id: number; amount: number; transaction_id: string;
  agent_note: string | null; state: string; created_at: string;
};

type PendingWithdrawalRow = {
  id: number; amount: number; upi_id: string;
  whatsapp_number: string | null;
  agent_note: string | null; state: string; created_at: string;
};

type HistoryRechargeRow = PendingRechargeRow & {
  decided_at: string | null;
  decision_note: string | null;
  ledger_id: number | null;
};
type HistoryWithdrawalRow = PendingWithdrawalRow & {
  decided_at: string | null;
  decision_note: string | null;
  paid_transaction_id: string | null;
  paid_at: string | null;
};

// Phase 17.2 — new 3rd sub-tab 'deposit' surfaces the security-deposit
// ledger (every row in agent_deposit_ledger for this agent), with the
// custom note from manual entries prominently displayed.
type Sub = 'pending' | 'history';

// Phase B Group 4 (2026-05-12): kinds now carry translation KEYS
// instead of hardcoded labels. The renderer resolves via useT() at
// row time, falling back to a humanised raw enum if the kind isn't
// in the dictionary (so a new ledger kind shipped by the backend
// never crashes the panel — it shows the raw enum and a translator
// adds the key in the next pass).
const KIND_META: Record<string, { labelKey: string; icon: string }> = {
  topup_from_admin:    { labelKey: 'agent.wallet.ledger.kind.topupFromAdmin',   icon: 'add_circle' },
  topup_to_player:     { labelKey: 'agent.wallet.ledger.kind.topupToPlayer',    icon: 'send' },
  topup_from_player:   { labelKey: 'agent.wallet.ledger.kind.topupFromPlayer',  icon: 'payments' },
  revenue_share:       { labelKey: 'agent.wallet.ledger.kind.revenueShare',     icon: 'pie_chart' },
  co_fund_offer:       { labelKey: 'agent.wallet.ledger.kind.coFundOffer',      icon: 'redeem' },
  admin_adjust:        { labelKey: 'agent.wallet.ledger.kind.adminAdjust',      icon: 'settings' },
  withdrawal_hold:     { labelKey: 'agent.wallet.ledger.kind.withdrawalHold',   icon: 'lock_clock' },
  withdrawal_paid:     { labelKey: 'agent.wallet.ledger.kind.withdrawalPaid',   icon: 'check_circle' },
  withdrawal_refund:   { labelKey: 'agent.wallet.ledger.kind.withdrawalRefund', icon: 'undo' },
};

const LIMIT = 30;
const inr = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`;
const fmtDate = (s: string) => new Date(s).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });

// ─── Panel ─────────────────────────────────────────────────────────

export default function MyWalletPanel() {
  const t = useT();
  // Use the global agentWallet store so the hero balance is in lock-
  // step with the chrome chip. The local ledger fetch below also
  // returns a balance but we trust the store for the headline.
  const { balance, loading: balanceLoading } = useAgentWallet();
  const [rows, setRows] = useState<LedgerRow[]>([]);
  const [pendingRechargeRows, setPendingRechargeRows] = useState<PendingRechargeRow[]>([]);
  const [pendingWithdrawalRows, setPendingWithdrawalRows] = useState<PendingWithdrawalRow[]>([]);
  const [historyRechargeRows, setHistoryRechargeRows] = useState<HistoryRechargeRow[]>([]);
  const [historyWithdrawalRows, setHistoryWithdrawalRows] = useState<HistoryWithdrawalRow[]>([]);

  const [sub, setSub] = useState<Sub>('pending');
  const [openForm, setOpenForm] = useState<'none' | 'recharge' | 'withdrawal'>('none');

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      // U4 (2026-07-09): deposit-ledger fetch removed with the duplicate
      // deposit sub-tab — /profile/deposit owns that surface now.
      const [walletR, prP, pwP, hrP, hwP] = await Promise.all([
        adminApi.get<{ rows: LedgerRow[]; total: number }>(`/admin/me-agent/wallet?limit=${LIMIT}`),
        adminApi.get<{ rows: PendingRechargeRow[] }>('/admin/me-agent/recharges?state=pending&limit=20').catch(() => ({ rows: [] })),
        adminApi.get<{ rows: PendingWithdrawalRow[] }>('/admin/me-agent/withdrawals?state=pending&limit=20').catch(() => ({ rows: [] })),
        adminApi.get<{ rows: HistoryRechargeRow[] }>('/admin/me-agent/recharges?limit=30').catch(() => ({ rows: [] })),
        adminApi.get<{ rows: HistoryWithdrawalRow[] }>('/admin/me-agent/withdrawals?limit=30').catch(() => ({ rows: [] })),
      ]);
      setRows(walletR.rows);
      setPendingRechargeRows(prP.rows);
      setPendingWithdrawalRows(pwP.rows);
      // History endpoints return all states; filter out pending so the
      // history tab is only completed activity.
      setHistoryRechargeRows((hrP.rows || []).filter((r) => r.state !== 'pending'));
      setHistoryWithdrawalRows((hwP.rows || []).filter((r) => r.state !== 'pending'));
    } catch (e) { setErr((e as ApiError).message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    refreshAgentWallet();
    load();
  }, [load]);

  function onSubmitted() {
    setOpenForm('none');
    refreshAgentWallet();
    load();
  }

  const pendingCount = pendingRechargeRows.length + pendingWithdrawalRows.length;

  return (
    <div className="space-y-4">
      {/* ─── 1. Hero balance card ─────────────────────────────── */}
      <HeroBalanceCard
        balance={balance}
        loading={balanceLoading}
      />

      {/* ─── 2. Action row ────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-2.5">
        <ActionButton
          icon="arrow_upward"
          label={t('agent.wallet.action.recharge')}
          active={openForm === 'recharge'}
          onClick={() => setOpenForm(openForm === 'recharge' ? 'none' : 'recharge')}
        />
        <ActionButton
          icon="arrow_downward"
          label={t('agent.wallet.action.withdraw')}
          active={openForm === 'withdrawal'}
          onClick={() => setOpenForm(openForm === 'withdrawal' ? 'none' : 'withdrawal')}
        />
      </div>

      <AnimatePresence initial={false}>
        {openForm === 'recharge' && (
          <RequestRechargeForm
            key="recharge-form"
            onSubmitted={onSubmitted}
            onCancel={() => setOpenForm('none')}
          />
        )}
        {openForm === 'withdrawal' && (
          <RequestWithdrawalForm
            key="withdrawal-form"
            balance={balance}
            onSubmitted={onSubmitted}
            onCancel={() => setOpenForm('none')}
          />
        )}
      </AnimatePresence>

      {/* ─── 3. Pending / History sub-tabs ────────────────────── */}
      {/* U4 (2026-07-09): the nested 'deposit' sub-tab is GONE — the same
          surface was reachable twice (here AND at /profile/deposit), an IA
          failure the owner flagged. The security deposit now lives ONLY on
          its own Profile card → /profile/deposit (which shows the full
          agent_deposit_ledger, including manual parent/admin entries). */}
      <MagentaTabBar
        size="sm"
        accent="brand"
        activeKey={sub}
        onChange={(k) => setSub(k as Sub)}
        items={[
          { key: 'pending', label: t('agent.wallet.tab.pending'), icon: 'inbox',   count: pendingCount },
          { key: 'history', label: t('agent.wallet.tab.history'), icon: 'history' },
        ]}
      />

      {/* ─── 4 + 5. Body ──────────────────────────────────────── */}
      {err && <Banner tone="error">{err}</Banner>}
      {sub === 'pending' && (
        <PendingFeed
          loading={loading}
          recharges={pendingRechargeRows}
          withdrawals={pendingWithdrawalRows}
        />
      )}
      {sub === 'history' && (
        <HistoryFeed
          loading={loading}
          ledger={rows}
          recharges={historyRechargeRows}
          withdrawals={historyWithdrawalRows}
        />
      )}
    </div>
  );
}

// ─── Hero balance card ─────────────────────────────────────────────

function HeroBalanceCard({ balance, loading }: { balance: number; loading: boolean }) {
  const t = useT();
  const animated = useCountUp(balance);
  const display = Math.round(animated);
  const isNegative = balance < 0;

  return (
    // U1 (2026-07-09): plain <section>, no JS opacity entrance — the hero
    // must be readable from first paint even when rAF is throttled.
    <section
      className={`relative overflow-hidden rounded-2xl p-5
                  ring-1 text-white
                  ${isNegative
                    ? 'bg-gradient-to-br from-rose-500 to-rose-700 ring-rose-300/40 shadow-[0_10px_30px_-12px_rgba(244,63,94,0.55)]'
                    : 'bg-gradient-to-br from-brand-500 to-brand-700 ring-brand-300/40 shadow-[0_10px_30px_-12px_rgba(var(--brand-rgb),0.55)]'}`}
    >
      {/* Decorative blur — soft highlight in the top-right */}
      <div aria-hidden className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-white/15 blur-2xl" />
      <div className="relative">
        <div className="flex items-center gap-2 mb-1.5">
          <Icon name="account_balance_wallet" size={16} className="opacity-90" />
          <span className="text-[10.5px] uppercase tracking-[0.18em] font-bold opacity-90">
            {t('agent.wallet.hero.label')}
          </span>
        </div>
        {/* Phase R7 follow-up (2026-05-14): when the balance is
            negative, render a scared-face emoji NEXT TO the amount
            so the agent understands why the bottom-nav profile tab
            is showing the same scared face. Same Unicode glyph
            (😨) at both surfaces — a deliberate visual breadcrumb
            from the warning back to its cause. The emoji is sized
            slightly smaller than the amount so it reads as an
            annotation rather than competing for attention; a subtle
            pulse echoes the bottom-nav badge animation. */}
        <div className="font-display font-black text-4xl sm:text-5xl tabular-nums leading-none
                        flex items-center gap-3 flex-wrap">
          <span>
            {loading ? '…' : (isNegative ? `−₹${Math.abs(display).toLocaleString('en-IN')}` : `₹${display.toLocaleString('en-IN')}`)}
          </span>
          {isNegative && !loading && (
            <motion.span
              initial={{ scale: 0.6, opacity: 0, rotate: 0 }}
              animate={{
                opacity: 1,
                scale:  [1, 1.14, 0.96, 1.10, 1],
                rotate: [0, -10, 8, -5, 5, 0],
              }}
              transition={{
                opacity: { duration: 0.25 },
                scale:  { duration: 1.4, repeat: Infinity, ease: 'easeInOut' },
                rotate: { duration: 1.4, repeat: Infinity, ease: 'easeInOut' },
              }}
              aria-hidden="true"
              className="text-3xl sm:text-4xl leading-none select-none
                         drop-shadow-[0_2px_6px_rgba(0,0,0,0.25)]"
            >
              😨
            </motion.span>
          )}
        </div>
        <p className="text-[12.5px] mt-2 text-white/85 leading-snug max-w-md">
          {t('agent.wallet.hero.helper')}
        </p>
      </div>
    </section>
  );
}

function ActionButton({
  icon, label, active, onClick,
}: {
  icon: string; label: string; active: boolean; onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-1.5 rounded-2xl px-4 py-3
                  text-[13px] font-bold transition active:scale-[0.98] border
                  ${active
                    ? 'bg-brand-500 text-white border-brand-500 shadow-[0_2px_10px_rgba(var(--brand-rgb),0.45)]'
                    : 'bg-white dark:bg-arena-700/40 text-arena-800 dark:text-white border-arena-200/60 dark:border-arena-600/50 hover:bg-arena-50/40 dark:hover:bg-arena-700/55'}`}
    >
      <Icon name={icon} size={16} />
      {label}
    </button>
  );
}

// ─── Pending feed ─────────────────────────────────────────────────

function PendingFeed({
  loading, recharges, withdrawals,
}: {
  loading: boolean;
  recharges: PendingRechargeRow[];
  withdrawals: PendingWithdrawalRow[];
}) {
  const t = useT();
  if (loading && recharges.length === 0 && withdrawals.length === 0) {
    return <CardShell>
      <div className="px-4 py-6 text-center text-[13px] text-arena-600 dark:text-neon-200/70">{t('agent.wallet.pending.loading')}</div>
    </CardShell>;
  }
  if (recharges.length === 0 && withdrawals.length === 0) {
    return <CardShell>
      <div className="px-4 py-8 text-center">
        <Icon name="inbox" size={28} className="text-arena-500 dark:text-neon-200/60 mx-auto mb-2" />
        <div className="text-[13px] text-arena-600 dark:text-neon-200/80">
          {t('agent.wallet.pending.empty')}
        </div>
      </div>
    </CardShell>;
  }
  // Merge chronologically.
  type Row = { kind: 'recharge' | 'withdrawal'; row: PendingRechargeRow | PendingWithdrawalRow };
  const merged: Row[] = [
    ...recharges.map((r): Row => ({ kind: 'recharge', row: r })),
    ...withdrawals.map((r): Row => ({ kind: 'withdrawal', row: r })),
  ].sort((a, b) => b.row.created_at.localeCompare(a.row.created_at));

  return (
    <ul className="grid grid-cols-1 gap-2.5">
      {merged.map(({ kind, row }) => (
        <li key={`${kind}-${row.id}`}>
          {kind === 'recharge'
            ? <PendingRechargeCard row={row as PendingRechargeRow} />
            : <PendingWithdrawalCard row={row as PendingWithdrawalRow} />}
        </li>
      ))}
    </ul>
  );
}

function PendingRechargeCard({ row }: { row: PendingRechargeRow }) {
  const t = useT();
  return (
    <CardShell>
      <div className="flex items-center gap-3 p-3.5">
        <span className="grid place-items-center w-9 h-9 rounded-xl
                         bg-emerald-50 dark:bg-emerald-400/15 text-emerald-700 dark:text-emerald-300
                         ring-1 ring-emerald-200 dark:ring-emerald-400/30 shrink-0">
          <Icon name="arrow_upward" size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-semibold text-arena-900 dark:text-white">
            {t('agent.wallet.pending.recharge.title', { amount: inr(row.amount) })}
          </div>
          <div className="text-[11px] text-arena-600 dark:text-neon-200/75 truncate">
            {t('agent.wallet.pending.recharge.meta', { txn: row.transaction_id, when: fmtDate(row.created_at) })}
          </div>
          {row.agent_note && <div className="text-[11px] text-arena-600 dark:text-neon-200/75 italic truncate">"{row.agent_note}"</div>}
        </div>
        <PendingBadge />
      </div>
    </CardShell>
  );
}

function PendingWithdrawalCard({ row }: { row: PendingWithdrawalRow }) {
  const t = useT();
  return (
    <CardShell>
      <div className="flex items-center gap-3 p-3.5">
        <span className="grid place-items-center w-9 h-9 rounded-xl
                         bg-rose-50 dark:bg-rose-400/15 text-rose-700 dark:text-rose-300
                         ring-1 ring-rose-200 dark:ring-rose-400/30 shrink-0">
          <Icon name="arrow_downward" size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-semibold text-arena-900 dark:text-white">
            {t('agent.wallet.pending.withdrawal.title', { amount: inr(row.amount) })}
          </div>
          <div className="text-[11px] text-arena-600 dark:text-neon-200/75 truncate">
            {t('agent.wallet.pending.withdrawal.meta', { upi: row.upi_id, when: fmtDate(row.created_at) })}
          </div>
          {row.agent_note && <div className="text-[11px] text-arena-600 dark:text-neon-200/75 italic truncate">"{row.agent_note}"</div>}
        </div>
        <PendingBadge />
      </div>
    </CardShell>
  );
}

function PendingBadge() {
  const t = useT();
  return (
    <span className="shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold
                     bg-amber-100 text-amber-800 dark:bg-amber-400/20 dark:text-amber-200">
      {t('agent.wallet.pending.badge')}
    </span>
  );
}

// ─── History feed ─────────────────────────────────────────────────

function HistoryFeed({
  loading, ledger, recharges, withdrawals,
}: {
  loading: boolean;
  ledger: LedgerRow[];
  recharges: HistoryRechargeRow[];
  withdrawals: HistoryWithdrawalRow[];
}) {
  const t = useT();
  type Item =
    | { kind: 'ledger';     row: LedgerRow;             ts: string }
    | { kind: 'recharge';   row: HistoryRechargeRow;    ts: string }
    | { kind: 'withdrawal'; row: HistoryWithdrawalRow;  ts: string };
  const merged: Item[] = [
    ...ledger.map((r): Item => ({ kind: 'ledger', row: r, ts: r.created_at })),
    ...recharges.map((r): Item => ({ kind: 'recharge', row: r, ts: r.decided_at || r.created_at })),
    ...withdrawals.map((r): Item => ({ kind: 'withdrawal', row: r, ts: r.paid_at || r.decided_at || r.created_at })),
  ].sort((a, b) => b.ts.localeCompare(a.ts));

  if (loading && merged.length === 0) {
    return <CardShell>
      <div className="px-4 py-6 text-center text-[13px] text-arena-600 dark:text-neon-200/70">{t('agent.wallet.history.loading')}</div>
    </CardShell>;
  }
  if (merged.length === 0) {
    return <CardShell>
      <div className="px-4 py-8 text-center">
        <Icon name="receipt_long" size={28} className="text-arena-500 dark:text-neon-200/60 mx-auto mb-2" />
        <div className="text-[13px] text-arena-600 dark:text-neon-200/80">
          {t('agent.wallet.history.empty')}
        </div>
      </div>
    </CardShell>;
  }
  return (
    <ul className="grid grid-cols-1 gap-2.5">
      {merged.map((it) => (
        <li key={`${it.kind}-${it.row.id}`}>
          {it.kind === 'ledger'     && <LedgerCard row={it.row as LedgerRow} />}
          {it.kind === 'recharge'   && <HistoryRechargeCard row={it.row as HistoryRechargeRow} />}
          {it.kind === 'withdrawal' && <HistoryWithdrawalCard row={it.row as HistoryWithdrawalRow} />}
        </li>
      ))}
    </ul>
  );
}

function LedgerCard({ row }: { row: LedgerRow }) {
  const t = useT();
  const meta = KIND_META[row.kind];
  // Unknown ledger kind fallback: humanise the raw enum (snake_case → spaces).
  const label = meta ? t(meta.labelKey) : row.kind.replace(/_/g, ' ');
  const icon  = meta?.icon || 'circle';
  const dir: 'pos' | 'neg' | 'neu' = row.amount > 0 ? 'pos' : row.amount < 0 ? 'neg' : 'neu';
  return (
    <CardShell>
      <div className="flex items-center gap-3 p-3.5">
        <span className={`grid place-items-center w-9 h-9 rounded-xl ring-1 shrink-0
                          ${dir === 'pos'
                            ? 'bg-emerald-50 dark:bg-emerald-400/15 text-emerald-700 dark:text-emerald-300 ring-emerald-200 dark:ring-emerald-400/30'
                            : dir === 'neg'
                              ? 'bg-rose-50 dark:bg-rose-400/15 text-rose-700 dark:text-rose-300 ring-rose-200 dark:ring-rose-400/30'
                              : 'bg-arena-100/60 dark:bg-arena-700/40 text-arena-700 dark:text-neon-200/85 ring-arena-200 dark:ring-arena-600/50'}`}>
          <Icon name={icon} size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-semibold text-arena-900 dark:text-white truncate">
            {label}
          </div>
          {row.note && (
            <div className="text-[11px] text-arena-600 dark:text-neon-200/75 truncate">"{row.note}"</div>
          )}
          <div className="text-[10.5px] text-arena-500 dark:text-neon-200/70 mt-0.5">{fmtDate(row.created_at)}</div>
        </div>
        <div className="text-right shrink-0">
          <div className={`font-mono font-bold tabular-nums text-[14px]
                            ${dir === 'pos' ? 'text-emerald-700 dark:text-emerald-300'
                              : dir === 'neg' ? 'text-rose-700 dark:text-rose-300'
                              : 'text-arena-700 dark:text-white'}`}>
            {row.amount > 0 ? '+' : row.amount < 0 ? '−' : ''}
            {inr(Math.abs(row.amount))}
          </div>
          {row.balance_after != null && (
            <div className="text-[10px] text-arena-500 dark:text-neon-200/70 tabular-nums">
              {t('agent.wallet.ledger.balanceAfter', { amount: inr(row.balance_after) })}
            </div>
          )}
        </div>
      </div>
    </CardShell>
  );
}

function HistoryRechargeCard({ row }: { row: HistoryRechargeRow }) {
  const t = useT();
  const accepted = row.state === 'approved';
  return (
    <CardShell>
      <div className="flex items-center gap-3 p-3.5">
        <span className={`grid place-items-center w-9 h-9 rounded-xl ring-1 shrink-0
                          ${accepted
                            ? 'bg-emerald-50 dark:bg-emerald-400/15 text-emerald-700 dark:text-emerald-300 ring-emerald-200 dark:ring-emerald-400/30'
                            : 'bg-rose-50 dark:bg-rose-400/15 text-rose-700 dark:text-rose-300 ring-rose-200 dark:ring-rose-400/30'}`}>
          <Icon name="arrow_upward" size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-semibold text-arena-900 dark:text-white">
            {t(accepted ? 'agent.wallet.history.recharge.titleAccepted' : 'agent.wallet.history.recharge.titleRejected', { amount: inr(row.amount) })}
          </div>
          <div className="text-[11px] text-arena-600 dark:text-neon-200/75 truncate">
            {t('agent.wallet.history.recharge.meta', { txn: row.transaction_id, when: fmtDate(row.decided_at || row.created_at) })}
          </div>
          {row.decision_note && <div className="text-[11px] text-arena-600 dark:text-neon-200/75 italic truncate">"{row.decision_note}"</div>}
        </div>
        <StateBadge state={row.state} />
      </div>
    </CardShell>
  );
}

function StateBadge({ state }: { state: string }) {
  const t = useT();
  const label =
    state === "pending"  ? t("agent.playerRequests.state.pending")
    : state === "rejected" ? t("agent.playerRequests.state.rejected")
    : state === "expired"  ? t("agent.playerRequests.state.expired")
    : state === "paid"     ? t("agent.playerRequests.state.manuallyPaid")
    : state === "approved" ? t("agent.playerRequests.state.accepted")
    : state;
  const tone =
    state === "pending"  ? "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-400/15 dark:text-amber-200 dark:border-amber-400/30"
    : state === "rejected" ? "bg-rose-50 text-rose-800 border-rose-200 dark:bg-rose-400/15 dark:text-rose-200 dark:border-rose-400/30"
    : state === "expired"  ? "bg-arena-100/70 text-arena-700 border-arena-200/60 dark:bg-arena-700/40 dark:text-neon-200/80 dark:border-arena-600/50"
    : "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-400/15 dark:text-emerald-200 dark:border-emerald-400/30";
  return (
    <span className={"inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-bold border " + tone}>
      {label}
    </span>
  );
}

function HistoryWithdrawalCard({ row }: { row: HistoryWithdrawalRow }) {
  const t = useT();
  const paid = row.state === 'paid';
  return (
    <CardShell>
      <div className="flex items-center gap-3 p-3.5">
        <span className={`grid place-items-center w-9 h-9 rounded-xl ring-1 shrink-0
                          ${paid
                            ? 'bg-emerald-50 dark:bg-emerald-400/15 text-emerald-700 dark:text-emerald-300 ring-emerald-200 dark:ring-emerald-400/30'
                            : 'bg-rose-50 dark:bg-rose-400/15 text-rose-700 dark:text-rose-300 ring-rose-200 dark:ring-rose-400/30'}`}>
          <Icon name="arrow_downward" size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-semibold text-arena-900 dark:text-white">
            {t(paid ? 'agent.wallet.history.withdrawal.titlePaid' : 'agent.wallet.history.withdrawal.titleRejected', { amount: inr(row.amount) })}
          </div>
          <div className="text-[11px] text-arena-600 dark:text-neon-200/75 truncate">
            {t('agent.wallet.history.withdrawal.meta', { upi: row.upi_id, when: fmtDate(row.paid_at || row.decided_at || row.created_at) })}
          </div>
          {row.paid_transaction_id && paid && (
            <div className="text-[10.5px] text-emerald-700 dark:text-emerald-300 font-mono truncate">{t('agent.wallet.history.withdrawal.paidTxn', { txn: row.paid_transaction_id })}</div>
          )}
          {row.decision_note && <div className="text-[11px] text-arena-600 dark:text-neon-200/75 italic truncate">"{row.decision_note}"</div>}
        </div>
        <StateBadge state={row.state} />
      </div>
    </CardShell>
  );
}

// ─── Shells ──────────────────────────────────────────────────────

function CardShell({ children }: { children: ReactNode }) {
  // U1 (2026-07-09): no JS opacity entrance — content readable from first paint.
  return (
    <div
      className="rounded-2xl
                 bg-white dark:bg-arena-700/40
                 border border-arena-200/60 dark:border-arena-600/40
                 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none"
    >
      {children}
    </div>
  );
}

function Banner({ tone, children }: { tone: 'error' | 'success'; children: ReactNode }) {
  const M = {
    error:   'bg-rose-50 border-rose-200 text-rose-800 dark:bg-rose-500/10 dark:border-rose-400/30 dark:text-rose-200',
    success: 'bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-500/10 dark:border-emerald-400/30 dark:text-emerald-200',
  } as const;
  return (
    <div className={`rounded-2xl border px-4 py-3 text-[13px] ${M[tone]}`}>{children}</div>
  );
}
